/** Airfield and air-defence structures: the hangar (mission 9) and the SAM launcher (mission 7). */

import { Vector3 } from 'three';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';

/**
 * Hangar footprint, metres: the sim's 14 x 13 box (`hx: 7, hz: 6.5`), deep enough for the 11 m
 * aircraft nose to tail with a metre to spare at either end. The open face is the south one.
 */
export const HANGAR_HALF_W = 7;
export const HANGAR_HALF_D = 6.5;
const HALF_W = HANGAR_HALF_W;
const HALF_D = HANGAR_HALF_D;
const EAVE = 2.2;
/** Thickness of the side and back walls: the clear floor inside is this much smaller all round. */
export const HANGAR_WALL = 0.3;
/**
 * The ranger walks INTO the hangar and fights there (mission 9), and the camera looks down
 * from the south at 45 degrees: a full roof would hide him and the aircraft. So only the
 * rear of the arch is skinned; the front bays are bare ribs you can see through. Set this to
 * `HALF_D` for a closed roof once tall objects dither-fade in front of the player (05 §8.1).
 */
const SKIN_TO_Z = -1;
/** Bare ribs stand this far apart over the open bays: four of them from the skin to the doorway. */
const RIB_SPACING = 2.5;
/** Arch profile across the span: [x, height]. Six facets give the toon shader a band each. */
const ARCH: ReadonlyArray<readonly [number, number]> = [
  [-7, EAVE], [-5, 4.16], [-2.6, 4.8], [0, 5.0], [2.6, 4.8], [5, 4.16], [7, EAVE],
];

/** How far a rib hangs below the arch: the clear height under it is the arch less this. */
const RIB_DROP = 0.45;

const v = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);

/** One arch rib at `z`: a south-facing fascia and a strip on top, so it shows from the front and from above. */
function rib(mb: MeshBuilder, p: Palette, z: number, marked: boolean): void {
  for (let i = 0; i + 1 < ARCH.length; i++) {
    const a = ARCH[i];
    const b = ARCH[i + 1];
    if (!a || !b) continue;
    const drop = RIB_DROP;
    const crown = i === 2 || i === 3;
    // The front rib carries the enemy's colour over the doorway: whose hangar it is.
    const fascia = p.color(marked && crown ? 'enemyAccent.0' : 'metal.lo');
    mb.quad(v(a[0], a[1] - drop, z), v(b[0], b[1] - drop, z), v(b[0], b[1], z), v(a[0], a[1], z), fascia);
    mb.quad(v(a[0], a[1], z), v(b[0], b[1], z), v(b[0], b[1], z - 0.4), v(a[0], a[1], z - 0.4), p.color('metal.mid'));
  }
}

/** Clear height under the ribs at `x`: what a tail fin has to pass beneath. */
export const hangarClearance = (x: number): number => archAt(x) - RIB_DROP;

/** Height of the arch at `x`, by straight interpolation along its facets. */
function archAt(x: number): number {
  for (let i = 0; i + 1 < ARCH.length; i++) {
    const a = ARCH[i];
    const b = ARCH[i + 1];
    if (a && b && x >= a[0] && x <= b[0]) return a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0]);
  }
  return EAVE;
}

export const hangar: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const skin = p.color('metal.mid');
  const inner = p.color('metal.lo');
  const back = -HALF_D;

  // Concrete floor: says "inside" and sets the aircraft off from the snow.
  mb.box(HALF_W * 2 - 0.6, 0.06, HALF_D * 2, p.color('concrete.mid'));
  // Side walls; their inward faces take the dark step so the interior reads as shade.
  const side = { all: skin, top: p.color('metal.hi') };
  mb.box(HANGAR_WALL, EAVE, HALF_D * 2, { ...side, right: inner }, place(-HALF_W + HANGAR_WALL / 2, 0, 0));
  mb.box(HANGAR_WALL, EAVE, HALF_D * 2, { ...side, left: inner }, place(HALF_W - HANGAR_WALL / 2, 0, 0));
  // Back wall: a box to the eaves, then the arch filled in above it.
  mb.box(HALF_W * 2, EAVE, HANGAR_WALL, { all: skin, front: inner }, place(0, 0, back + HANGAR_WALL / 2));

  for (let i = 0; i + 1 < ARCH.length; i++) {
    const a = ARCH[i];
    const b = ARCH[i + 1];
    if (!a || !b) continue;
    const z = back + HANGAR_WALL;
    mb.quad(v(a[0], EAVE, z), v(b[0], EAVE, z), v(b[0], b[1], z), v(a[0], a[1], z), inner);
    // Roof skin over the rear bays.
    mb.quad(v(a[0], a[1], SKIN_TO_Z), v(b[0], b[1], SKIN_TO_Z), v(b[0], b[1], back), v(a[0], a[1], back), skin);
  }
  // Open-ribbed front bays: the rib that ends the skin, the bare ones, and the marked one over the doorway.
  for (let z = SKIN_TO_Z; z < HALF_D - 1; z += RIB_SPACING) rib(mb, p, z, false);
  rib(mb, p, HALF_D, true);
  if (SKIN_TO_Z < HALF_D - 1) {
    // Purlins tie the bare ribs back to the skinned bays.
    for (const x of [-5, 0, 5]) {
      const len = HALF_D - SKIN_TO_Z;
      mb.box(0.2, 0.2, len, p.color('metal.lo'), place(x, archAt(x) - 0.2, SKIN_TO_Z + len / 2));
    }
  }

  if (p.biome === 'arctic') {
    // Inset drifts lying on the upper facets; the curve of bare roof round them stays visible.
    const drifts: ReadonlyArray<readonly [number, number, number, number]> = [
      [-2.3, -0.4, -5.9, -3.5], [0.5, 2.3, -4.7, -1.9], [-4.6, -3.0, -4.1, -1.5],
    ];
    for (const [x0, x1, z0, z1] of drifts) {
      const y0 = archAt(x0) + 0.07;
      const y1 = archAt(x1) + 0.07;
      mb.quad(v(x0, y0, z1), v(x1, y1, z1), v(x1, y1, z0), v(x0, y0, z0), p.color('ground.3'));
    }
  }
  return mb;
};

/**
 * SAM launcher (mission 7): turntable on a pad, a rail pitched up to the south with two or
 * three missiles on it. The warm tips are the brightest thing in a desert frame, on purpose.
 */
export const samLauncher: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.cylinder(1.6, 1.6, 0.35, 10, p.color('concrete.lo'), undefined, p.color('concrete.mid'));
  mb.cylinder(1.15, 1.05, 0.5, 8, p.color('metal.mid'), place(0, 0.35, 0), p.color('metal.hi'));
  mb.box(0.8, 0.9, 0.9, p.color('metal.lo'), place(0, 0.85, -0.1));
  const missiles = rng.chance(0.6) ? 3 : 2;
  // 0.96 rad off vertical = 35 degrees of elevation. A little seeded traverse so a battery is not a row of clones.
  mb.with(place(0, 1.5, -0.2, rng.range(-0.4, 0.4), 1, 0.96), () => {
    // In this frame +y runs along the missiles (up and south) and +z is the side beneath them.
    mb.box(0.62 * missiles, 2.4, 0.16, p.color('metal.lo'), place(0, -1.2, 0.26));
    for (let i = 0; i < missiles; i++) {
      const x = (i - (missiles - 1) / 2) * 0.6;
      mb.cylinder(0.17, 0.17, 2.5, 6, p.color('metal.hi'), place(x, -1.25, 0), p.color('ink.1'));
      mb.cylinder(0.19, 0.19, 0.22, 6, p.color('fire.2'), place(x, 0.85, 0));
      mb.cylinder(0.17, 0, 0.55, 6, p.color('fire.0'), place(x, 1.25, 0));
      mb.box(0.54, 0.4, 0.05, p.color('metal.mid'), place(x, -1.2, 0)); // tail fins, one pair is enough
    }
  });
  return mb;
};
